import { useEffect, useRef, type RefObject } from "react";

const PULL_DISTANCE = 76;
const MAX_PULL = 132;
const DIRECTION_DISTANCE = 10;
const VERTICAL_RATIO = 1.3;
const CHILD_CONTROLS = "input, textarea, select, [contenteditable], [role=slider], [role=listbox], canvas, video, [data-pull-refresh-ignore]";

export function refreshInteractionBlocked(): boolean {
  return Boolean(document.querySelector('[data-refresh-mutation="true"]')
    || Array.from(document.querySelectorAll('dialog[open], [aria-modal="true"], details[open], [role="menu"]')).some(element => element.getClientRects().length > 0)
    || document.activeElement?.matches("input, textarea, select, [contenteditable]"));
}

function atScrollTop(root: HTMLElement, target: Element): boolean {
  for (let element: Element | null = target; element && element !== root; element = element.parentElement) {
    const style = getComputedStyle(element);
    // A nested scroll surface or a child-owned gesture must keep its own input.
    if (style.touchAction === "none" || (/auto|scroll/.test(style.overflowY) && element.scrollHeight > element.clientHeight)) return false;
  }
  const scrollableRoot = /auto|scroll/.test(getComputedStyle(root).overflowY);
  if (scrollableRoot) return root.scrollTop <= 0;
  // Inbox boundaries live inside their existing scrolling pane. Its scroll
  // position, rather than the fixed document, determines top-pull admission.
  for (let ancestor = root.parentElement; ancestor; ancestor = ancestor.parentElement) {
    if (/auto|scroll/.test(getComputedStyle(ancestor).overflowY) && ancestor.scrollHeight > ancestor.clientHeight) return ancestor.scrollTop <= 0;
  }
  return (document.scrollingElement?.scrollTop ?? window.scrollY) <= 0;
}

/** Own intentional top-of-surface pulls; disable/identity changes cancel without replacing caller content. */
export function usePullGesture(root: RefObject<HTMLDivElement>, disabled: boolean, refresh: () => void, identity: string) {
  const callbacks = useRef({ disabled, refresh });
  callbacks.current = { disabled, refresh };
  const progress = useRef<(distance: number) => void>(() => {});

  useEffect(() => {
    const element = root.current;
    if (!element) return;
    let gesture: { id: number; x: number; y: number; target: Element; distance: number } | null = null;
    const reset = () => { gesture = null; progress.current(0); };
    const blocked = () => callbacks.current.disabled || refreshInteractionBlocked();
    const start = (event: TouchEvent) => {
      reset();
      const target = event.target;
      if (event.touches.length !== 1 || blocked() || !(target instanceof Element)
        || target.closest(CHILD_CONTROLS) || !atScrollTop(element, target)) return;
      const touch = event.touches[0];
      gesture = { id: touch.identifier, x: touch.clientX, y: touch.clientY, target, distance: 0 };
    };
    const move = (event: TouchEvent) => {
      if (!gesture) return;
      const touch = Array.from(event.touches).find(candidate => candidate.identifier === gesture?.id);
      if (!touch || event.touches.length !== 1 || blocked() || !atScrollTop(element, gesture.target)) { reset(); return; }
      const vertical = touch.clientY - gesture.y;
      const horizontal = Math.abs(touch.clientX - gesture.x);
      if (Math.max(Math.abs(vertical), horizontal) < DIRECTION_DISTANCE) {
        gesture.distance = 0; progress.current(0); return;
      }
      if (vertical <= 0 || vertical < horizontal * VERTICAL_RATIO || !event.cancelable) { reset(); return; }
      // A deliberate pull may start on a link/button; cancelling movement suppresses
      // its click, while ordinary taps keep their native action.
      event.preventDefault();
      // beUI's exponential resistance approaches the cap without a hard stop.
      gesture.distance = MAX_PULL * (1 - Math.exp(-vertical / MAX_PULL));
      progress.current(gesture.distance);
    };
    const end = (event: TouchEvent) => {
      const ready = gesture && event.touches.length === 0 && gesture.distance >= PULL_DISTANCE && !blocked();
      reset();
      if (ready) callbacks.current.refresh();
    };
    element.addEventListener("touchstart", start, { passive: true });
    element.addEventListener("touchmove", move, { passive: false });
    element.addEventListener("touchend", end);
    element.addEventListener("touchcancel", reset);
    return () => {
      reset();
      element.removeEventListener("touchstart", start);
      element.removeEventListener("touchmove", move);
      element.removeEventListener("touchend", end);
      element.removeEventListener("touchcancel", reset);
    };
  }, [root, disabled, identity]);

  return { progress, pullDistance: PULL_DISTANCE };
}
